/** Default TTL for dynamic command-search payloads (15 minutes). */
export const COMMAND_SEARCH_CACHE_TTL_MS = 15 * 60 * 1000;

interface CacheEntry<T> {
    data: T;
    expiresAt: number;
}

/**
 * In-memory TTL cache for command-search dynamic data with prefix-based invalidation.
 */
class CommandSearchCache {
    private readonly store = new Map<string, CacheEntry<unknown>>();

    /** Returns cached data when present and not expired. */
    public get<T>(key: string): T | null {
        const entry = this.store.get(key);
        if (!entry || Date.now() > entry.expiresAt) {
            this.store.delete(key);
            return null;
        }
        return entry.data as T;
    }

    /** Stores data with an optional custom TTL. */
    public set<T>(key: string, data: T, ttlMs = COMMAND_SEARCH_CACHE_TTL_MS): void {
        this.store.set(key, {
            data,
            expiresAt: Date.now() + ttlMs,
        });
    }

    /** Removes a single cache entry. */
    public invalidate(key: string): void {
        this.store.delete(key);
    }

    /** Removes all entries whose keys start with the given prefix. */
    public invalidateByPrefix(prefix: string): void {
        for (const key of this.store.keys()) {
            if (key.startsWith(prefix)) {
                this.store.delete(key);
            }
        }
    }

    /** Removes entries matching the predicate. */
    public invalidateMatching(matcher: (key: string) => boolean): void {
        for (const key of this.store.keys()) {
            if (matcher(key)) {
                this.store.delete(key);
            }
        }
    }

    /** Clears the entire cache. */
    public clear(): void {
        this.store.clear();
    }
}

export const commandSearchCache = new CommandSearchCache();

/** @deprecated Use commandSearchCache */
export const panelCommandSearchCache = commandSearchCache;

/** @deprecated Use COMMAND_SEARCH_CACHE_TTL_MS */
export const PANEL_COMMAND_SEARCH_CACHE_TTL_MS = COMMAND_SEARCH_CACHE_TTL_MS;
